import { useMemo } from 'react';

import dayjs from 'dayjs';

import { DATE_TIME_SLASH } from '@cast/constants';
import { DatePeriodInTimestamp, FilterPreset } from '@cast/design-system';

import { useIsWoopDemoMode } from 'features/workload-optimization';
import { useTimeRangeSelection } from 'hooks/useTimeRangeSelection';
import {
  DateRangeFilter,
  RangePickerQueryParamKeys,
} from '@cast/shared-date-ui';

export const RIGHTSIZING_QUERY_PARAM_KEYS: RangePickerQueryParamKeys = {
  fromDate: 'rightsizing_chart_from',
  toDate: 'rightsizing_chart_to',
  dateRangePreset: 'rightsizing_chart_date_preset',
  timeZone: 'rightsizing_chart_timezone',
};

export const RIGHTSIZING_FILTER_PRESETS = [
  DateRangeFilter.Last15Mins,
  DateRangeFilter.Last30Mins,
  DateRangeFilter.LastHour,
  DateRangeFilter.Last24Hours,
  DateRangeFilter.Last7Days,
] as const;

export type RightsizingFilterPreset =
  (typeof RIGHTSIZING_FILTER_PRESETS)[number];

export const RIGHTSIZING_DEFAULT_FILTER_PRESET: RightsizingFilterPreset =
  DateRangeFilter.LastHour;

// todo(mike) move higher and fix imports that is used
export const useRightsizingTimeRangeSelection = () => {
  const isWoopDemoMode = useIsWoopDemoMode();

  const constraints = useMemo(
    () => ({
      //woop keeps data only up tp 8 weeks
      minDate: ({ timezone }: { timezone: string }) =>
        dayjs()
          .tz(timezone)
          .subtract(7 * 8 - 1, 'days')
          .startOf('day')
          .toISOString(),
      maxDate: () => dayjs.utc().toISOString(),
      maxNumberOfDays: 8,
    }),
    []
  );

  return useTimeRangeSelection({
    queryParamKeys: RIGHTSIZING_QUERY_PARAM_KEYS,
    initialState: {
      preset: RIGHTSIZING_DEFAULT_FILTER_PRESET,
      timeZone: isWoopDemoMode ? 'UTC' : undefined,
    },
    constraints,
    allowTimeInput: true,
  });
};

export const formatRightsizingTimeRangeInputValue = (
  range: DatePeriodInTimestamp,
  timezone: string,
  preset?: FilterPreset
): string => {
  if (preset) {
    return preset.label || '';
  }

  if (range?.from && range?.to) {
    return `${dayjs
      .utc(range.from)
      .tz(timezone)
      .format(DATE_TIME_SLASH)} - ${dayjs
      .utc(range.to)
      .tz(timezone)
      .format(DATE_TIME_SLASH)}`;
  }

  return 'Select date';
};
